//@ts-check
import ky from 'ky';
import { getfetchopts } from '$lib/utils/utils.js';
import { showOverlay } from '$lib/overlay.svelte.js';

const basePath = import.meta.env.VITE_API_URL || '';

// Reactive session state. `member` is null when signed out; otherwise the profile + roles.
//
// `hint` is a NON-AUTHORITATIVE "probably signed in" flag, cached locally (IndexedDB) so public
// pages can label nav (footer, mobile drawer) without calling the auth API. It is for presentation
// only — never gate access on it. The authoritative check is /api/auth/me (loadSession), run when
// entering the member area, which corrects a stale hint.
export const auth = $state({
	loading: true,
	loaded: false,
	member: null,
	hint: false,
});

const AUTH_HINT_KEY = 'auth_hint';

/**
 * Persist (and reflect) whether the user appears signed in — for nav labeling only. Best-effort.
 * @param {boolean} signedIn
 */
function rememberSignedIn(signedIn) {
	auth.hint = signedIn;
	idbSet(AUTH_HINT_KEY, signedIn).catch((err) => console.warn('Could not persist auth hint', err));
}

/**
 * Hydrate the signed-in hint from local storage WITHOUT any network call. Call this at app boot on
 * public pages so nav reflects signed-in state without hitting /api/auth/me. Authoritative session
 * loading is still loadSession(), done when entering the member area.
 */
export async function hydrateAuthHint() {
	try {
		auth.hint = (await idbGet(AUTH_HINT_KEY)) === true;
	} catch {
		auth.hint = false;
	}
}

/**
 * Mark the user as signed in locally (e.g. right after the auth callback exchange).
 * @param {boolean} signedIn
 */
export function persistAuthHint(signedIn) {
	rememberSignedIn(signedIn);
}

// In-flight de-dupe: the root layout loads the session at app boot and the members layout also
// ensures it's loaded; concurrent callers share the same request rather than firing /me twice.
let inflight = null;

/**
 * Load the current session from /api/auth/me. The session cookie is sent automatically
 * (same-origin on the apex). A 401 means signed-out — leaves `member` null without throwing.
 * Concurrent calls return the same in-flight promise; once settled, a later call re-fetches.
 * @returns {Promise<void>}
 */
export function loadSession() {
	if (inflight) return inflight;
	inflight = (async () => {
		auth.loading = true;
		try {
			const fetchopts = await getfetchopts();
			const member = await ky.get(`${basePath}/api/auth/me`, fetchopts).json();
			auth.member = member;
			rememberSignedIn(true);
			// FUTURE — session/config persistence (planning marker, no action needed yet):
			// Today the profile + permissions live only in memory and are re-fetched from /me on every
			// full page load. Once the app caches heavier user config/session state, persist it to the
			// 'staac-auth' IndexedDB store (idbGet/idbSet below) and hydrate from it here for instant
			// loads / offline PWA use, then refresh from /me in the background. See the matching marker
			// in routes/auth/callback/+page.svelte and businessmind's session.js for the fuller pattern.
		} catch (err) {
			auth.member = null;
			rememberSignedIn(false);
			if (!err.isautherr) {
				console.error('Failed to load session', err);
			}
		} finally {
			auth.loading = false;
			auth.loaded = true;
			inflight = null;
		}
	})();
	return inflight;
}

/**
 * Whether the signed-in member holds a permission. Permissions are the effective (unioned) grants
 * from the member's roles, resolved server-side and delivered on /api/auth/me. "*" grants all.
 * This mirrors permcheck() on the API; the API still enforces server-side (defense-in-depth).
 * @param {string} permission
 */
export function can(permission) {
	const perms = auth.member?.permissions;
	if (!Array.isArray(perms)) return false;
	return perms.includes('*') || perms.includes(permission);
}

/**
 * Begin the sign-in flow: bounce to the apex /login page with a return URL.
 * @param {string} [returnUrl]
 */
export function goauth(returnUrl) {
	const target = returnUrl || (typeof window !== 'undefined' ? window.location.pathname : '/');
	window.location.href = `/login?redirect=${encodeURIComponent(target)}`;
}

/** Sign out: delete the server session, clear local state, return home. */
export async function logout() {
	// Cloak the page immediately so the click registers, then keep it up through the navigation.
	showOverlay('Signing out…');
	try {
		const fetchopts = await getfetchopts();
		await ky.get(`${basePath}/api/auth/logout`, fetchopts).json();
	} catch (err) {
		console.warn('Logout request failed', err);
	}
	auth.member = null;
	rememberSignedIn(false);
	// Full navigation unloads the page (and the overlay with it) — no hideOverlay needed.
	window.location.href = '/';
}

// ---- IndexedDB-backed local store (survives across PWA sessions) ----
// Currently holds only device_id. This is the intended home for future durable session/user-config
// keys (see the planning markers in loadSession above and routes/auth/callback/+page.svelte): the
// generic kv store + idbGet/idbSet below already support adding keys without schema changes.
const DB_NAME = 'staac-auth';
const STORE = 'kv';
const DEVICE_KEY = 'device_id';

function openDb() {
	return new Promise((resolve, reject) => {
		const req = indexedDB.open(DB_NAME, 1);
		req.onupgradeneeded = () => req.result.createObjectStore(STORE);
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error);
	});
}

function idbGet(key) {
	return openDb().then(
		(db) =>
			new Promise((resolve, reject) => {
				const tx = db.transaction(STORE, 'readonly');
				const req = tx.objectStore(STORE).get(key);
				req.onsuccess = () => resolve(req.result);
				req.onerror = () => reject(req.error);
			})
	);
}

function idbSet(key, value) {
	return openDb().then(
		(db) =>
			new Promise((resolve, reject) => {
				const tx = db.transaction(STORE, 'readwrite');
				tx.objectStore(STORE).put(value, key);
				tx.oncomplete = () => resolve(value);
				tx.onerror = () => reject(tx.error);
			})
	);
}

/** Get (creating if needed) the persistent device id for this browser/PWA install. */
export async function getDeviceId() {
	try {
		let id = await idbGet(DEVICE_KEY);
		if (!id) {
			id = crypto.randomUUID();
			await idbSet(DEVICE_KEY, id);
		}
		return id;
	} catch (err) {
		console.warn('IndexedDB unavailable; using ephemeral device id', err);
		return crypto.randomUUID();
	}
}
